import React, { useMemo } from 'react';
import { Row, Col, Typography, Tag, Empty } from 'antd';
import LineChart from '../../../components/chart/LineChart';
import { TDashboardChildrenData } from '../../../models/user/Dashboard';

const { Title, Paragraph, Text } = Typography;

interface ChildrenContentProps {
    dashboardChildrenData: TDashboardChildrenData | null;
    palette: {
        primary: string;
        secondary: string;
        background: string;
        border: string;
        tab: string;
        muted: string;
    };
}
const ChildrenContent: React.FC<ChildrenContentProps> = ({ dashboardChildrenData, palette }) => {
    const info = dashboardChildrenData?.ongoing_lesson;
    const progressPercent = useMemo(() => {
        const series = dashboardChildrenData?.percentage_subject_done;
        if (!series || series.length === 0) {
            return 0;
        }
        const last = series[series.length - 1];
        return Math.round(last.value ?? 0);
    }, [dashboardChildrenData]);

    const lessonPath = useMemo(() => {
        if (!info) return null;
        const epic = info.epic?.name;
        return [epic, info.name].filter(Boolean).join(' • ');
    }, [info]);

    return (
        <Row gutter={[24, 24]} style={{ minHeight: 320 }}>
            <Col xs={24} xl={15}>
                <div
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 18,
                        padding: '24px 24px 20px',
                        borderRadius: 24,
                        background: '#fff',
                        border: `1px solid ${palette.border}`,
                        boxShadow: '0 18px 35px rgba(7,24,38,0.12)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>
                            Progression globale
                        </Title>
                        <Tag
                            color={palette.primary}
                            style={{
                                borderRadius: 999,
                                padding: '4px 12px',
                                fontWeight: 600,
                                background: `${palette.primary}20`,
                                border: `1px solid ${palette.primary}55`,
                            }}
                        >
                            {progressPercent}%
                        </Tag>
                    </div>
                    <div style={{ flex: 1, minHeight: 240 }}>
                        {dashboardChildrenData?.percentage_subject_done ? (
                            <LineChart
                                data={dashboardChildrenData.percentage_subject_done}
                                valueType="%"
                            />
                        ) : (
                            <Empty description="Aucune progression pour le moment" />
                        )}
                    </div>
                </div>
            </Col>

            <Col xs={24} xl={9}>
                <div
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        padding: '24px',
                        borderRadius: 24,
                        background: 'linear-gradient(135deg, rgba(45,156,201,0.12), rgba(139,190,30,0.1))',
                        border: `1px solid ${palette.border}`,
                        boxShadow: '0 18px 32px rgba(7,24,38,0.1)',
                    }}
                >
                    <Title level={4} style={{ margin: 0 }}>
                        Leçon active
                    </Title>
                    {info ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                }}
                            >
                                <Text type="secondary">Mission en cours</Text>
                                <Title level={3} style={{ margin: 0 }}>
                                    {info.name}
                                </Title>
                                {lessonPath && (
                                    <Paragraph style={{ marginBottom: 0, color: palette.muted }}>
                                        {lessonPath}
                                    </Paragraph>
                                )}
                            </div>
                            <div
                                style={{
                                    marginTop: 'auto',
                                    padding: '14px 16px',
                                    borderRadius: 18,
                                    background: 'rgba(255,255,255,0.65)',
                                    border: `1px dashed ${palette.primary}55`,
                                }}
                            >
                                <Text style={{ color: palette.muted }}>
                                    Une mise à jour du suivi détaillé des badges arrive bientôt.
                                </Text>
                            </div>
                        </>
                    ) : (
                        <Empty description="Aucune leçon en cours" />
                    )}
                </div>
            </Col>
        </Row>
    );
};

export default ChildrenContent;
